/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/lib/api/endpoints.ts
 * DESCRIPTION: API endpoint definitions from Swagger v2.0.0 spec
 *              Includes all production endpoints + custom extensions.
 *
 * ENDPOINT CONVENTIONS:
 * - Static paths: string literals (e.g., '/v1/upload')
 * - Dynamic paths: functions returning strings (e.g., (id) => `/v1/identity/${id}`)
 * - Query params: appended by caller or via helper functions
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 2.1.0
 * @created 2026-04-03
 * @updated 2026-04-12 - Reformatted: removed separator comments,
 *                       shortened end-of-line comments only,
 *                       synced with backend-reviewed endpoint list
 * ─────────────────────────────────────────────────────────────────────
 */

export const ENDPOINTS = {
  // System
  HEALTH: '/health', // Health check
  READY: '/ready', // Readiness check
  SWAGGER_UI: '/swagger/index.html', // Swagger UI
  SWAGGER_JSON: '/swagger/doc.json', // Swagger spec JSON
  ROUTER_INIT: '/', // Router initialization

  // Internal
  INTERNAL_BULK_ROUTES: '/internal/routes/bulk', // Register bulk routes
  INTERNAL_STORAGE_INIT: '/internal/storage/init', // Init storage backend
  INTERNAL_POSTGRES_INIT: '/storage/postgres/init', // Init PostgreSQL storage

  // Upload
  UPLOAD: '/v1/upload', // Single file upload
  UPLOAD_BULK: '/v1/upload/bulk', // Bulk file upload
  UPLOAD_URL: '/v1/upload/url',
  UPLOAD_BULK_STATUS: (id: string) => `/v1/upload/bulk/${id}`, // Get bulk status
  UPLOAD_BULK_CANCEL: (id: string) => `/v1/upload/bulk/${id}`, // Cancel bulk upload
  RESULTS: '/v1/results', // Poll analysis results

  // Analyzer
  ANALYZER_EXTRACT: '/analyzer/extract', // Extract file metadata

  // Identities
  IDENTITIES_LIST: '/v1/identities', // List identities with filters
  IDENTITY_DETAIL: (id: string) => `/v1/identity/${id}`, // Get single identity
  IDENTITY_DELETE: (id: string) => `/v1/identity/${id}`, // Delete identity
  IDENTITY_CHAIN: (id: string) => `/v1/identity/${id}/chain`, // Get verification chain
  IDENTITY_TRUST: (id: string) => `/v1/identity/${id}/trust`, // Calculate trust score
  IDENTITY_RIGHTS: (id: string) => `/v1/identity/${id}/rights`, // Update rights metadata
  IDENTITY_SEARCH: '/v1/identity/search', // Search identities
  IDENTITY_VERIFY: '/v1/identity/verify', // Verify identity

  // Identity metadata updates (telemetry prep)
  IDENTITY_METADATA: (id: string) => `/v1/identity/${id}/metadata`,  // PATCH for retrospective updates

  // Re-analyze an existing identity
  // POST /v1/identity/{id}/re-analyze
  //IDENTITY_REANALYZE: (id: string) => `/v1/identity/${id}/re-analyze`,
  // Download certified (watermarked) file
  // GET /storage/local/{persistent_id}
  //IDENTITY_DOWNLOAD: (id: string) => `/storage/local/${id}`,

    // Identity Management
  //IDENTITY_DETAIL: (id: string) => `/v1/identity/${id}`,
  IDENTITY_SIMILAR: (id: string) => `/v1/identity/${id}/similar`,
  IDENTITY_DOWNLOAD: (id: string) => `/storage/local/${id}`,
  IDENTITY_DEEPFAKE_CHECK: (id: string) => `/v1/identity/${id}/deepfake-check`,

  // VMIE Intelligence Scanning
  IDENTITY_VMIE_SCAN: (id: string) => `/v1/identity/${id}/vmie-scan`,
  IDENTITY_REANALYZE: (id: string) => `/v1/identity/${id}/re-analyze`,
  VMIE_RULES_RELOAD: '/v1/vmie/rules/reload',
  VMIE_STATUS: '/v1/vmie/status',


  FINGERPRINT_SEARCH: (hash: string, threshold?: number) => {
    const url = '/v1/identity/fingerprint';
    const params = new URLSearchParams({ hash });
    if (threshold !== undefined) params.append('threshold', threshold.toString());
    return `${url}?${params.toString()}`;
  }, // Find by fingerprint hash

  // Analytics
  ANALYTICS_SUMMARY: '/v1/analytics/summary', // Get analytics summary
  ANALYTICS_TIMESERIES: '/v1/analytics/timeseries', // Get time-series data
  ANALYTICS_DISTRIBUTION: '/v1/analytics/distribution', // Get distribution breakdown
  ANALYTICS_PROVIDERS: '/v1/analytics/providers', // Get AI provider stats

  // Telemetry
  TELEMETRY_METRICS: '/v1/telemetry/metrics', // Get real-time metrics
  TELEMETRY_EVENTS: '/v1/telemetry/events', // Get recent events
  TELEMETRY_GEO: '/v1/telemetry/geo', // Get geographic data
  TELEMETRY_TOOLS: '/v1/telemetry/tools', // Get tools leaderboard
  TELEMETRY_CATEGORIES: '/v1/telemetry/categories', // Get category breakdown
  TELEMETRY_TAGS: '/v1/telemetry/tags', // Get trending tags
  TELEMETRY_STREAM: '/v1/telemetry/stream', // WebSocket for live metrics

  // AI Providers
  AI_PROVIDERS: '/v1/ai/providers', // List available providers
  AI_PROVIDER_HEALTH: (provider: string) => `/v1/ai/providers/${provider}/health`, // Check provider health
} as const;

// ─────────────────────────────────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────────────────────────────────
export const buildIdentityListQuery = (filters: {
  // Pagination
  page?: number;
  limit?: number;
  
  // Basic filters
  file_type?: string;
  status?: string;
  trust_score_min?: number;
  trust_score_max?: number;
  date_from?: string;
  date_to?: string;
  search?: string;
  
  // JSONB path filters
  origin_is_ai_generated?: boolean;
  origin_is_original?: boolean;
  fingerprint_algorithm?: string;
  watermark_type?: string;
  anomalies_contains?: string;
  tags_contains?: string;
  
  // Sorting
  sort_by?: string;
  sort_dir?: string;
}) => {
  const params = new URLSearchParams();
  
  // Pagination
  if (filters.page) params.append('page', filters.page.toString());
  if (filters.limit) params.append('limit', filters.limit.toString());
  
  // Basic filters
  if (filters.file_type) params.append('file_type', filters.file_type);
  if (filters.status) params.append('status', filters.status);
  if (filters.trust_score_min !== undefined) params.append('trust_score_min', filters.trust_score_min.toString());
  if (filters.trust_score_max !== undefined) params.append('trust_score_max', filters.trust_score_max.toString());
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);
  if (filters.search) params.append('search', filters.search);
  
  // JSONB path filters
  if (filters.origin_is_ai_generated !== undefined) params.append('origin_is_ai_generated', filters.origin_is_ai_generated.toString());
  if (filters.origin_is_original !== undefined) params.append('origin_is_original', filters.origin_is_original.toString());
  if (filters.fingerprint_algorithm) params.append('fingerprint_algorithm', filters.fingerprint_algorithm);
  if (filters.watermark_type) params.append('watermark_type', filters.watermark_type);
  if (filters.anomalies_contains) params.append('anomalies_contains', filters.anomalies_contains);
  if (filters.tags_contains) params.append('tags_contains', filters.tags_contains);
  
  // Sorting
  if (filters.sort_by) params.append('sort_by', filters.sort_by);
  if (filters.sort_dir) params.append('sort_dir', filters.sort_dir);
  
  return params.toString();
};

export const buildFingerprintQuery = (hash: string, threshold?: number) => {
  const params = new URLSearchParams({ hash });
  if (threshold !== undefined) params.append('threshold', threshold.toString());
  return params.toString();
};

// ─────────────────────────────────────────────────────────────────────
// Type Exports
// ─────────────────────────────────────────────────────────────────────
export type EndpointKey = keyof typeof ENDPOINTS;
export type DynamicEndpoint = (...args: any[]) => string;
